import React, { useCallback, useMemo, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import Platform from '@lyearn/core/utils/Platform';
import { useTranslation } from '@lyearn/i18n';
import { Button, Stack } from '@lyearn/ui';
import CourseQuizPlayerHeader from '@/modules/corporate/modules/course/components/QuizCardContainer/components/CourseQuizPlayerHeader';
import { QuizDetailsContentFragment } from '@/modules/corporate/modules/quiz/views/QuizDetails/graphql/fragments/generated/QuizDetailsContent';
import { QuizDetailsPerformanceFragment } from '@/modules/corporate/modules/quiz/views/QuizDetails/graphql/fragments/generated/QuizDetailsPerformance';
import useRetakeQuiz from '@/modules/corporate/modules/quiz/views/QuizDetails/hooks/useRetakeQuiz';
import { QuizState } from '@/modules/corporate/modules/quiz/views/QuizDetails/types';
import { getCanRetakeQuiz } from '@/modules/corporate/modules/quiz/views/QuizDetails/utils/getCanRetakeQuiz';
import { View } from '@/modules/corporate/modules/quiz/views/QuizPlayer/hooks/useQuizPlayer/types';
import { QuizPlayerProps } from '@/modules/corporate/modules/quiz/views/QuizPlayer/types';

const isNative = Platform.OS !== 'web';
type PopupQuizPlayerProps = Pick<QuizPlayerProps, 'config' | 'renderers'>;

interface Params {
  quiz: QuizDetailsContentFragment;
  quizPerformance?: QuizDetailsPerformanceFragment;
  parentEntityId: string;
}

const usePopupQuiz = ({ parentEntityId, quiz, quizPerformance }: Params) => {
  const { t } = useTranslation();
  const [showQuizPlayer, showQuizPlayerActions] = useToggle(false);
  const [showModal, showModalActions] = useToggle(false);
  const [initialScreen, setScreen] = useState<View>();
  const [reviewAttemptId, setReviewAttemptId] = useState<string | undefined>(undefined);
  const [quizState] = useState<QuizState>({ showQuiz: false });

  const { fetching: fetchingRetakeQuiz, executeRetakeQuiz } = useRetakeQuiz({
    parentEntityId,
    quizId: quiz.id,
    isEmbed: true,
  });

  const onOpen = useCallback(() => {
    showQuizPlayerActions.off();
    showModalActions.on();
  }, [showModalActions, showQuizPlayerActions]);

  const onClose = useCallback(() => {
    showQuizPlayerActions.off();
    showModalActions.off();
  }, [showModalActions, showQuizPlayerActions]);

  const { canRetakeQuiz } = getCanRetakeQuiz(quiz, quizPerformance);

  const onRetakeQuizAndToggle = useCallback(async () => {
    await executeRetakeQuiz();
    showQuizPlayerActions.on();
  }, [executeRetakeQuiz, showQuizPlayerActions]);

  const quizPlayerProps = useMemo<PopupQuizPlayerProps>(
    () => ({
      config: { animateQuestion: true, showQuestionIndex: true, resumeQuiz: true },
      renderers: {
        renderHeader: (params) => <CourseQuizPlayerHeader {...params} onClose={onClose} />,
        renderReviewActions: (params) => (
          <Stack className="mt-48 xs:mt-0 xs:flex-1" gapX={8}>
            {canRetakeQuiz ? (
              <Button
                appearance="secondary"
                className="xs:flex-1 xs:me-12"
                size="large"
                variant="outlined"
                onClick={showQuizPlayerActions.off}>
                {t('QuizPlayer:ReAttempt')}
              </Button>
            ) : isNative ? (
              <Button
                className="xs:flex-1 xs:me-12"
                size="large"
                onClick={params?.onReviewQuestions}>
                {t('QuizPlayer:Review.Review')}
              </Button>
            ) : null}
            <Button
              className="xs:flex-1"
              size="large"
              variant={canRetakeQuiz || isNative ? 'outlined' : 'filled'}
              onClick={onClose}>
              {t('QuizPlayer:BacktoLesson')}
            </Button>
          </Stack>
        ),
      },
      reviewAttemptId,
    }),
    [reviewAttemptId, onClose, canRetakeQuiz, showQuizPlayerActions.off, t],
  );

  const onShowAttemptHistory = React.useCallback(() => {
    showQuizPlayerActions.on();
  }, [showQuizPlayerActions]);

  return {
    showModal,
    showQuizPlayer,
    onOpen,
    onClose,
    quizPlayerProps,
    screen: initialScreen,
    onRetakeQuiz: showQuizPlayerActions.off,
    quizState,
    reviewAttemptId,
    onShowQuiz: showQuizPlayerActions.on,
    onCloseQuiz: showQuizPlayerActions.off,
    onShowAttemptHistory,
    onRetakeQuizAndToggle,
    fetchingRetakeQuiz,
  };
};

export default usePopupQuiz;
